import React from 'react';
import { Drawer } from 'vaul';
import { cn } from '@/lib/cn';
import { AppBar } from '@/components/ui/AppBar';

type DrawerRootProps = React.ComponentPropsWithoutRef<typeof Drawer.Root>;

export interface BottomSheetProps extends Omit<DrawerRootProps, 'children'> {
  /**
   * Mandatory label shown at the top of the sheet and exposed to assistive tech.
   */
  label: string;
  /** Optional subtitle shown in the app bar. */
  subtitle?: string;
  /** Optional leading slot or back handler for the app bar. */
  leading?: React.ReactNode;
  /** Optional trailing slot or close handler for the app bar. */
  trailing?: React.ReactNode;
  /** Click handler to show a default back icon button on the left. */
  onBack?: () => void;
  /**
   * Optional trigger element rendered using `asChild`.
   */
  trigger?: React.ReactNode;
  /**
   * Main scrollable body content.
   */
  children: React.ReactNode;
  /**
   * Optional footer area rendered under the body (e.g. buttons).
   */
  footer?: React.ReactNode;
  /**
   * Toggle the drag handle shown at the top of the sheet.
   */
  showHandle?: boolean;
  /**
   * Optional callback when the close button is pressed.
   */
  onClose?: () => void;
  /**
   * When true, the sheet expands to full viewport height (useful for multi-step flows like Auth).
   * Default: false
   */
  fullHeight?: boolean;
  /**
   * Extra classes for overlay, content container, body and footer slots.
   */
  overlayClassName?: string;
  contentClassName?: string;
  bodyClassName?: string;
  footerClassName?: string;
  closeOnOverlayClick?: boolean;
}

export function BottomSheet({
  label,
  leading,
  trailing,
  onBack,
  trigger,
  children,
  footer,
  showHandle = true,
  onClose,
  fullHeight = false,
  overlayClassName,
  contentClassName,
  handleOnly = true,
  bodyClassName,
  footerClassName,
  subtitle = '',
  dismissible = true,
  closeOnOverlayClick = true,
  ...rootProps
}: BottomSheetProps) {
  return (
    <Drawer.Root
      dismissible={dismissible}
      handleOnly={handleOnly}
      {...(rootProps as Omit<DrawerRootProps, 'fadeFromIndex'>)}
    >
      {trigger ? <Drawer.Trigger asChild>{trigger}</Drawer.Trigger> : null}
      <Drawer.Portal>
        <Drawer.Overlay
          onClick={closeOnOverlayClick ? onClose : undefined}
          className={cn('fixed inset-0 z-40 bg-black/50', overlayClassName)}
        />
        <Drawer.Content
          className={cn(
            // Position & Layout
            fullHeight
              ? 'fixed inset-x-0 top-2 bottom-0 z-50 !h-[calc(100dvh-8px)]'
              : 'fixed inset-x-0 !bottom-0 z-50 mt-24 !h-auto',
            'flex flex-col overflow-hidden',
            // Dimensions
            fullHeight ? '!max-h-none' : 'max-h-[calc(100dvh-16px)]',
            // Appearance
            'rounded-t-3xl border border-secondary bg-primary shadow-lg',
            contentClassName,
          )}
        >
          <div
            className={cn(
              // Position & Layout
              'relative flex flex-1 flex-col pt-1',
              // Overflow
              'overflow-hidden',
            )}
          >
            {showHandle ? <Drawer.Handle /> : null}
            {/* Accessible title for assistive tech */}
            <Drawer.Title className="flex-shrink-0">
              <AppBar
                title={label}
                subtitle={subtitle}
                leading={leading}
                trailing={trailing}
                {...(onBack ? { onBack } : {})}
                {...(onClose ? { onClose } : {})}
              />
            </Drawer.Title>

            <div
              className={cn(
                // Layout & Flex
                'flex-1 min-h-0',
                // Overflow
                'overflow-y-auto flex-grow',
                // Spacing
                'px-6 py-4',
                bodyClassName,
              )}
            >
              {children}
            </div>

            {footer ? (
              <div
                className={cn(
                  // Layout
                  'flex-shrink-0',
                  // Appearance
                  'border-t border-secondary',
                  footerClassName,
                )}
              >
                {footer}
              </div>
            ) : null}
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
